import { get, isArray, isNil } from 'lodash/fp';
import { createSelector } from '@reduxjs/toolkit';
import {
    getCurrentProjectSlug,
    getCurrentCombinedSlug,
    isWorkbench,
    isSqlEditor
} from 'app/routes';
import { resolveIds } from 'app/store/utils';
import { selectDatasetColumnFilterForEntityId } from 'app/store/columnFilter/selectors';
import { applySchemaFilter } from 'app/store/transformers.js';
import { DATA_SOURCE_REFERENCE_DTO_TYPE } from 'app/store/__generated__/types';

export const selectEntitiesById = get('entities.byEntityId');

export const selectEntityIdsBySlug = get('entities.bySlug');

const selectPropId = (_, props) => props?.entityId;

export const selectEntityByPropId = createSelector(selectPropId, selectEntitiesById, get);
export const selectEntityById = createSelector((_, id) => id, selectEntitiesById, get);

export const selectEntity = createSelector(
    [getCurrentProjectSlug, getCurrentCombinedSlug, selectEntitiesById, selectEntityIdsBySlug],
    (spaceSlug, slug, entitiesById, idsBySlug) => {
        if (isWorkbench() && isNil(slug)) {
            return entitiesById?.[idsBySlug[spaceSlug]];
        }

        if (isSqlEditor()) {
            return entitiesById?.[idsBySlug[spaceSlug]];
        }

        if (isWorkbench()) {
            return entitiesById?.[idsBySlug[slug]];
        }
    }
);

export const selectEntityId = createSelector(selectEntity, get('entityId'));

export const selectEntityDescriptionByPropId = createSelector(
    selectEntityByPropId,
    get('description')
);

const OP_REGEX = /_op\d+$/;

export const selectSchemaForPropEntityId = createSelector(
    [selectEntitiesById, (state, props) => props.entityId],
    (byEntityId, entityId) => {
        if (OP_REGEX.test(entityId)) {
            // We have an operation id in a stack...
            const baseEntityId = entityId.replace(OP_REGEX, '');
            const opUid = entityId.split('.').at(-1);
            return byEntityId[baseEntityId]?.schemasByUid[opUid]?.columns;
        }

        const columnIds = byEntityId[entityId]?.columnIds;
        return isArray(columnIds) ? resolveIds(byEntityId, columnIds) : null;
    }
);

export const selectColumnTypeForPropEntityId = createSelector(
    [selectEntitiesById, (state, props) => props.entityId],
    (byEntityId, entityId) => byEntityId[entityId]?.typeFamily
);

export const selectFilteredSchemaForPropEntityId = createSelector(
    selectSchemaForPropEntityId,
    selectDatasetColumnFilterForEntityId,
    applySchemaFilter
);

export const selectHasEntityDescriptionUpdateFailed = get('entities.entityDescriptionUpdateFailed');

const selectEntityIdForDeletion = get('entities.deleteAssetDialog.entityIdForDeletion');

export const selectEntityForDeletion = createSelector(
    selectEntityIdForDeletion,
    selectEntitiesById,
    (entityId, entities) => entities[entityId]
);

export const selectEntityDeletionFailure = get('entities.deleteAssetDialog.failures');

export const selectDeleteEntityInProgress = get('entities.deleteAssetDialog.inProgress');
export const selectIsDeleteAssetDialogOpen = createSelector(
    selectEntityIdForDeletion,
    (entityId) => !isNil(entityId)
);

export const selectIsDatasourceTypeByPropId = createSelector(
    selectPropId,
    selectEntitiesById,
    (entityId, entities) => entities[entityId]?._type === DATA_SOURCE_REFERENCE_DTO_TYPE
);
